import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import serviceOrdersApi from '../../../api/serviceOrdersApi';
import { applyServerErrors, getErrorMessage } from '../../../utils/errorHandler';
import { serviceOrderStatusLabel } from '../../../utils/serviceOrderStatuses';

const baseSchema = {
  client: yup.object().nullable().required('Selecciona un cliente'),
  technician_id: yup.mixed().nullable(),
  service_id: yup.number().typeError('Selecciona un servicio').required('Selecciona un servicio'),
  description: yup.string().trim().required('Describe el problema o servicio solicitado'),
};

const createSchema = yup.object(baseSchema);
const editSchema = yup.object({
  ...baseSchema,
  status: yup.number().typeError('Selecciona un estado').required('Selecciona un estado'),
  note: yup.string().nullable(),
});

const emptyValues = {
  client: null,
  technician_id: '',
  service_id: '',
  description: '',
  status: '',
  note: '',
};

// Formulario de alta/edicion de una orden de servicio. `order` nulo significa "crear nueva"
// (siempre arranca en el estado "recibido" del lado del backend).
export default function ServiceOrderFormDialog({ open, onClose, onSaved, order, services, statuses, technicians }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(order);

  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [searchingClients, setSearchingClients] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(isEditing ? editSchema : createSchema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    if (order) {
      setClientOptions(order.client ? [order.client] : []);
      reset({
        client: order.client || null,
        technician_id: order.technician?.id || '',
        service_id: order.service?.id || '',
        description: order.description || '',
        status: order.status?.id || '',
        note: '',
      });
    } else {
      setClientOptions([]);
      reset(emptyValues);
    }
  }, [open, order, reset]);

  // Busca clientes conforme el usuario escribe, con un pequeno debounce.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const timeout = setTimeout(async () => {
      setSearchingClients(true);
      try {
        const { data } = await serviceOrdersApi.searchClients(clientSearch);
        setClientOptions(data.data);
      } catch (error) {
        // Silencioso: no bloquea el formulario si la busqueda falla puntualmente.
      } finally {
        setSearchingClients(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [open, clientSearch]);

  const onSubmit = async (data) => {
    const payload = {
      user_id: data.client.id,
      technician_id: data.technician_id || null,
      service_id: data.service_id,
      description: data.description,
    };
    if (isEditing) {
      payload.status = data.status;
      payload.note = data.note || undefined;
    }

    try {
      if (isEditing) {
        await serviceOrdersApi.updateServiceOrder(order.id, payload);
        enqueueSnackbar('Orden de servicio actualizada correctamente.', { variant: 'success' });
      } else {
        await serviceOrdersApi.createServiceOrder(payload);
        enqueueSnackbar('Orden de servicio registrada correctamente.', { variant: 'success' });
      }
      onSaved();
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      if (!handled) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo guardar la orden de servicio.'), { variant: 'error' });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Editar orden de servicio' : 'Nueva orden de servicio'}</DialogTitle>

      <DialogContent dividers>
        <Stack component="form" id="service-order-form" spacing={2.5} onSubmit={handleSubmit(onSubmit)} noValidate>
          {errors.root?.message && <Alert severity="error">{errors.root.message}</Alert>}

          <Controller
            name="client"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={clientOptions}
                value={field.value}
                loading={searchingClients}
                getOptionLabel={(option) => (option ? `${option.name}${option.email ? ` (${option.email})` : ''}` : '')}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, value) => field.onChange(value)}
                onInputChange={(event, value) => setClientSearch(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    placeholder="Busca por nombre, correo o teléfono"
                    error={Boolean(errors.client)}
                    helperText={errors.client?.message}
                  />
                )}
              />
            )}
          />

          <Controller
            name="technician_id"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Técnico asignado (opcional)" fullWidth>
                <MenuItem value="">Sin asignar</MenuItem>
                {technicians.map((tech) => (
                  <MenuItem key={tech.id} value={tech.id}>
                    {tech.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="service_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Servicio"
                fullWidth
                error={Boolean(errors.service_id)}
                helperText={errors.service_id?.message}
              >
                {services.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.name} ({service.service_type?.name})
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <TextField
            label="Descripción"
            fullWidth
            multiline
            minRows={3}
            error={Boolean(errors.description)}
            helperText={errors.description?.message}
            {...register('description')}
          />

          {isEditing && (
            <>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Estado"
                    fullWidth
                    error={Boolean(errors.status)}
                    helperText={errors.status?.message}
                  >
                    {statuses.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {serviceOrderStatusLabel(s.name)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <TextField
                label="Nota del cambio de estado (opcional)"
                fullWidth
                {...register('note')}
              />
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" form="service-order-form" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ServiceOrderFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  order: PropTypes.object,
  services: PropTypes.array.isRequired,
  statuses: PropTypes.array.isRequired,
  technicians: PropTypes.array.isRequired,
};

ServiceOrderFormDialog.defaultProps = {
  order: null,
};
